import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Building2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", industry: "", spoc_name: "", spoc_email: "", spoc_phone: "",
  address: "", gstin: "", fee_percent: 8.33, contract_notes: "", is_active: true,
};

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/clients", { params: { q: q || undefined } }).then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/clients/${data.id}`, data);
      else await api.post("/admin/clients", data);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/admin/clients/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-clients">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Clients</h1>
          <p className="text-sm text-slate-500">{total} companies</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="client-new"><Plus className="mr-1 h-4 w-4" />New client</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="clients-search" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Company</th><th className="px-4 py-3">SPOC</th><th className="px-4 py-3">Fee %</th><th className="px-4 py-3">Placements</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium text-slate-900"><Building2 className="h-4 w-4 text-primary" />{c.name}</div>
                  <div className="text-xs text-slate-500">{c.industry}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{c.spoc_name || "—"}</div>
                  <div className="text-xs text-slate-500">{c.spoc_email} · {c.spoc_phone}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{c.fee_percent}%</td>
                <td className="px-4 py-3 text-slate-700">{c.total_placements || 0}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(c)} data-testid={`client-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid={`client-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No clients yet. Add your first one.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-clients-pager" />

      {editing && <ClientDialog value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ClientDialog({ value, onClose, onSave }) {
  const [f, setF] = useState(value);
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input placeholder="Company name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="client-name" />
          <Input placeholder="Industry (e.g. Pharma)" value={f.industry} onChange={(e) => set("industry", e.target.value)} />
          <Input placeholder="SPOC name" value={f.spoc_name} onChange={(e) => set("spoc_name", e.target.value)} />
          <Input placeholder="SPOC email" value={f.spoc_email} onChange={(e) => set("spoc_email", e.target.value)} />
          <Input placeholder="SPOC phone" value={f.spoc_phone} onChange={(e) => set("spoc_phone", e.target.value)} />
          <Input placeholder="GSTIN" value={f.gstin} onChange={(e) => set("gstin", e.target.value)} />
          <Input type="number" step="0.01" placeholder="Fee %" value={f.fee_percent} onChange={(e) => set("fee_percent", parseFloat(e.target.value) || 0)} data-testid="client-fee" />
          <div />
          <Textarea placeholder="Registered address" value={f.address} onChange={(e) => set("address", e.target.value)} className="sm:col-span-2" rows={2} />
          <Textarea placeholder="Contract notes / commercial terms" value={f.contract_notes} onChange={(e) => set("contract_notes", e.target.value)} className="sm:col-span-2" rows={3} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(f)} data-testid="client-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
